///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ray-marched stylized cloud for the city weather vignette. The node's
/// transform scales the shared unit sphere into the cloud's ellipsoid; the
/// shader (vignette_cloud.glsl) marches in that proxy space, so the camera
/// and sun direction are handed over already transformed by the inverse
/// model matrix.
///

import { sharedEmberSphereGeometry } from "@/earthbrowser/entities/geometries/EmberSphereGeometry"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { normal, vec2, type vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

export interface CloudStyle {
	/** 0..1 fraction of sky covered */
	coverage: number
	/** 0..1 blend from scattered cumulus toward a flat deck */
	stratus: number
	/** 0..1 rain-laden gray */
	darkness: number
	/** Noise drift in proxy units per second (wind), x/z */
	drift: vec2
}

export class VignetteCloudNode extends NodeBase {
	private readonly cloudRenderer: CloudRenderer

	constructor(context: StovokorContext) {
		super()
		this.cloudRenderer = new CloudRenderer(context, sharedEmberSphereGeometry(context))
		this.renderer = this.cloudRenderer
	}

	public setStyle(style: CloudStyle): void {
		this.cloudRenderer.coverage.value = style.coverage
		this.cloudRenderer.stratus.value = style.stratus
		this.cloudRenderer.darkness.value = style.darkness
		this.cloudRenderer.drift.value = style.drift
	}

	/** Sine of the sun's elevation at the city (-1..1) */
	public set sunElevation(value: number) {
		this.cloudRenderer.sunElevation.value = value
	}

	/** 0..1 lightning glow inside the volume */
	public set flash(value: number) {
		this.cloudRenderer.flash.value = value
	}

	public set opacity(value: number) {
		this.cloudRenderer.opacity.value = value
	}

	public set timeSeconds(value: number) {
		this.cloudRenderer.time.value = value
	}
}

class CloudRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public cameraPosition = new UniformDynamic<vec3>()
	public lightDirection = new UniformDynamic<vec3>()
	public sunElevation = new UniformDynamic<number>(1)
	public time = new UniformDynamic<number>(0)
	public drift = new UniformDynamic<vec2>(new vec2(0.01, 0))
	public coverage = new UniformDynamic<number>(0)
	public stratus = new UniformDynamic<number>(0)
	public darkness = new UniformDynamic<number>(0)
	public flash = new UniformDynamic<number>(0)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("vignette_cloud"), geometry, [transparentMaterial])
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		// Proxy space: the march runs inside the unit sphere, so the camera
		// and the sun go through the inverse model (ellipsoid scale included)
		const inverse = state.modelMatrix.invert()
		this.cameraPosition.value = inverse.multVec3(state.camera.position, 1)
		this.lightDirection.value = normal(inverse.multVec3(RenderState.lightDirection, 0))
	}
}
